import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, Settings } from "lucide-react";
import { useSecureAuthContext } from "@/contexts/SecureAuthContext";
import NotificationBell from "@/components/NotificationBell";
import ModelsDropdown from "@/components/ModelsDropdown";
import { useSiteConfigValue } from "@/hooks/useSiteConfigValue";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, signOut } = useSecureAuthContext();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { value: logoUrl } = useSiteConfigValue('site_logo_url', '');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleLogoClick = () => {
    if (location.pathname === '/') {
      const heroElement = document.getElementById('hero');
      if (heroElement) {
        heroElement.scrollIntoView({
          behavior: 'smooth'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } else {
      navigate('/', {
        state: {
          scrollTo: 'hero'
        }
      });
    }
    setIsOpen(false);
  };
  const handleSectionClick = (sectionId: string) => {
    if (location.pathname === '/') {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      navigate('/', {
        state: {
          scrollTo: sectionId
        }
      });
    }
    setIsOpen(false);
  };
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsOpen(false);
  };
  const handleAdminClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(false);
    setTimeout(() => {
      navigate('/admin');
    }, 100);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-500 ease-out ${isScrolled ? 'bg-black/95 backdrop-blur-xl border-white/10 shadow-lg' : 'bg-transparent backdrop-blur-sm border-transparent'}`}>
      <nav className="container mx-auto px-3 xs:px-4 sm:px-6 md:px-6 lg:px-8 py-1 xs:py-1 sm:py-1 transition-all duration-500 ease-out">
        <div className="flex items-center justify-between">
          {/* Logo Section */}
          <button onClick={handleLogoClick} className="flex items-center space-x-2 xs:space-x-2 hover:opacity-80 transition-opacity min-w-0 w-auto max-w-fit touch-manipulation py-1 xs:py-2">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="h-6 xs:h-7 sm:h-8 w-auto max-w-[120px] sm:max-w-[160px] md:max-w-[200px] object-contain flex-shrink-0" onError={e => {
            // Fallback to default logo if custom logo fails to load
            (e.target as HTMLImageElement).style.display = 'none';
            const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }} /> : null}
            <div className={`w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 ${logoUrl ? 'hidden' : 'flex'}`}>
              <span className="text-white font-bold text-sm xs:text-sm sm:text-sm">BG</span>
            </div>
            {!logoUrl && <div className="flex flex-col items-start">
                <span className="text-base sm:text-lg md:text-xl font-bold text-white hidden md:block">
                  The Bulk Image Generator
                </span>
                <span className="text-xs sm:text-sm text-gray-300 hidden md:block text-left">From Ideas to Images, Instantly</span>
                <span className="text-sm xs:text-base sm:text-sm font-bold text-white block md:hidden truncate">
                  BIG
                </span>
              </div>}
          </button>

          {/* Desktop Navigation - Enterprise Level */}
          <div className="hidden lg:flex items-center space-x-8 xl:space-x-12">
            <button onClick={() => handleSectionClick('features')} className="text-gray-300 hover:text-white transition-colors duration-300 text-sm xl:text-base font-medium whitespace-nowrap">
              Features
            </button>
            <button onClick={() => handleSectionClick('pricing')} className="text-gray-300 hover:text-white transition-colors duration-300 text-sm xl:text-base font-medium whitespace-nowrap">
              Pricing
            </button>
            <Link to="/gallery" className="text-gray-300 hover:text-white transition-colors duration-300 text-sm xl:text-base font-medium whitespace-nowrap" onClick={() => setIsOpen(false)}>
              Gallery
            </Link>
            <Link to="/help" className="text-gray-300 hover:text-white transition-colors duration-300 text-sm xl:text-base font-medium whitespace-nowrap">
              Help
            </Link>
            <Link to="/help/faq" className="text-gray-300 hover:text-white transition-colors duration-300 text-sm xl:text-base font-medium whitespace-nowrap">
              FAQ
            </Link>
            <ModelsDropdown />
          </div>

          {/* User Menu / Auth Buttons */}
          <div className="hidden sm:flex items-center space-x-2 md:space-x-2 lg:space-x-3">
            {user ? <div className="flex items-center space-x-2 md:space-x-2">
                <NotificationBell />
                <Link to="/dashboard">
                  <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm md:text-sm px-3 md:px-3 py-2 md:py-2 h-10 md:h-auto touch-manipulation">
                    <User className="w-4 h-4 md:w-4 md:h-4 md:mr-1" />
                    <span className="hidden lg:inline">Dashboard</span>
                  </Button>
                </Link>
                <Link to="/settings">
                  <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm md:text-sm px-3 md:px-3 py-2 md:py-2 h-10 md:h-auto touch-manipulation">
                    <Settings className="w-4 h-4 md:w-4 md:h-4 md:mr-1" />
                    <span className="hidden xl:inline">Settings</span>
                  </Button>
                </Link>
                {isAdmin && <Button onClick={handleAdminClick} size="sm" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm md:text-sm px-3 md:px-3 py-2 md:py-2 h-10 md:h-auto touch-manipulation">
                    <span className="hidden xl:inline">Admin</span>
                    <span className="xl:hidden">A</span>
                  </Button>}
                <Button onClick={handleSignOut} size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm md:text-sm px-3 md:px-3 py-2 md:py-2 h-10 md:h-auto touch-manipulation">
                  <LogOut className="w-4 h-4 md:w-4 md:h-4 md:mr-1" />
                  <span className="hidden lg:inline">Out</span>
                </Button>
              </div> : <div className="flex items-center space-x-2 md:space-x-2">
                <Link to="/auth">
                  <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-sm md:text-sm px-3 md:px-3 py-2 md:py-2 h-10 md:h-auto touch-manipulation">
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth?mode=signup">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-sm md:text-sm px-3 md:px-3 py-2 md:py-2 h-10 md:h-auto touch-manipulation">
                    Start
                  </Button>
                </Link>
              </div>}
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden text-white p-3 xs:p-3 touch-manipulation hover:bg-white/10 rounded-lg transition-colors">
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && <div className="lg:hidden mt-4 xs:mt-5 py-4 xs:py-5 border-t border-white/10 max-h-[calc(100vh-80px)] overflow-y-auto bg-black/20 backdrop-blur-md rounded-lg mx-2 xs:mx-4">
            <div className="flex flex-col space-y-3 xs:space-y-4 px-3 xs:px-4">
              <button onClick={() => handleSectionClick('features')} className="text-gray-300 hover:text-white hover:bg-white/10 transition-all text-left py-3 xs:py-4 text-base xs:text-lg rounded-lg touch-manipulation">
                Features
              </button>
              <button onClick={() => handleSectionClick('pricing')} className="text-gray-300 hover:text-white hover:bg-white/10 transition-all text-left py-3 xs:py-4 text-base xs:text-lg rounded-lg touch-manipulation">
                Pricing
              </button>
              <Link to="/gallery" className="text-gray-300 hover:text-white hover:bg-white/10 transition-all py-3 xs:py-4 text-base xs:text-lg rounded-lg touch-manipulation" onClick={() => setIsOpen(false)}>
                Gallery
              </Link>
              <Link to="/help" className="text-gray-300 hover:text-white hover:bg-white/10 transition-all py-3 xs:py-4 text-base xs:text-lg rounded-lg touch-manipulation" onClick={() => setIsOpen(false)}>
                Help
              </Link>
              <Link to="/help/faq" className="text-gray-300 hover:text-white hover:bg-white/10 transition-all py-3 xs:py-4 text-base xs:text-lg rounded-lg touch-manipulation" onClick={() => setIsOpen(false)}>
                FAQ
              </Link>
              <div className="py-3 xs:py-4">
                <ModelsDropdown />
              </div>

              {user ? <div className="flex flex-col space-y-3 xs:space-y-4 pt-4 xs:pt-5 border-t border-white/10">
                  <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                    <Button size="sm" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base xs:text-lg h-12 xs:h-14 touch-manipulation active:scale-95 transition-transform">
                      <User className="w-5 h-5 mr-3" />
                      Dashboard
                    </Button>
                  </Link>
                  <Link to="/settings" onClick={() => setIsOpen(false)}>
                    <Button size="sm" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base xs:text-lg h-12 xs:h-14 touch-manipulation active:scale-95 transition-transform">
                      <Settings className="w-5 h-5 mr-3" />
                      Settings
                    </Button>
                  </Link>
                  {isAdmin && <Button onClick={handleAdminClick} size="sm" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-base xs:text-lg h-12 xs:h-14 touch-manipulation active:scale-95 transition-transform">
                      Admin Panel
                    </Button>}
                  <Button onClick={handleSignOut} size="sm" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base xs:text-lg h-12 xs:h-14 touch-manipulation active:scale-95 transition-transform">
                    <LogOut className="w-5 h-5 mr-3" />
                    Sign Out
                  </Button>
                </div> : <div className="flex flex-col space-y-3 xs:space-y-4 pt-4 xs:pt-5 border-t border-white/10">
                  <Link to="/auth" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20 text-base xs:text-lg h-12 xs:h-14 touch-manipulation active:scale-95 transition-transform">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/auth?mode=signup" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-base xs:text-lg h-12 xs:h-14 touch-manipulation active:scale-95 transition-transform">
                      Get Started
                    </Button>
                  </Link>
                </div>}
            </div>
          </div>}
      </nav>
    </header>;
};
export default Header;